/**
 * CleanupWrongPropagationDialog — spec iter162 (Feb 2026).
 *
 * Modale admin pour lancer le dry-run de nettoyage rétroactif du bug
 * de propagation « futurs uniquement » non respectée (iter161), puis
 * appliquer la correction avec backup automatique.
 *
 * Flow :
 *   1. Ouverture → dry-run automatique (POST ?apply=false)
 *   2. Rendu du rapport : total docs impactés, liste par type avec
 *      détail (référence, statut, étapes à retirer).
 *   3. Bouton « Appliquer la correction » actif seulement si
 *      docs_impacted_total > 0 → POST ?apply=true (backup + update).
 *   4. Rendu du rapport post-apply + toast succès.
 *
 * Sécurité :
 *   • Réservé au Premier Admin (bouton d'ouverture caché sinon,
 *     endpoint back 403 sinon).
 *   • Étapes déjà validées (closed_at) JAMAIS retirées côté back.
 *   • Backup de chaque doc dans documents_backup_iter161.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2, ShieldCheck, ChevronDown, ChevronRight, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";

const DEFAULT_TYPES = "Acte de vente final,Acte de vente VSP";
// Spec iter164s (2026-02-25) — Nombre d'étapes ORIGINAL par type
// (avant les propagations parasites d'iter161). Fallback heuristique
// activé sur le backend : tout doc avec `len(steps) > expected` est
// flaggé + corrigé (les étapes `closed_at` restent protégées).
// L'admin peut ajuster ces valeurs dans l'UI si nécessaire.
const DEFAULT_EXPECTED = {
  "Acte de vente VSP": 5,
  "Acte de vente final": 3,
};

export default function CleanupWrongPropagationDialog({ open, onOpenChange }) {
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [expected, setExpected] = useState(DEFAULT_EXPECTED);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [applying, setApplying] = useState(false);
  const [expandedType, setExpandedType] = useState(null);
  const [applied, setApplied] = useState(false);
  // Spec iter163 (Feb 2026) — état pour le rollback.
  const [confirmRollback, setConfirmRollback] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  // Spec iter164s — Exécute le dry-run/apply type par type (chaque type
  // a son propre `expected_step_count`), puis agrège les rapports. Ceci
  // contourne la limite backend « un seul expected pour tout le CSV ».
  //
  // Spec iter164u (2026-02-26) BUGFIX — 3 correctifs sur l'agrégation :
  //   1) Le backend renvoie `step_removals_total` (et non `steps_removed_total`),
  //      d'où KPI « Étapes à retirer » toujours à 0.
  //   2) `types_scanned` n'était pas agrégé → KPI « Types analysés » à 0.
  //   3) Les erreurs par type (`{ error: "Aucun type trouvé…" }`)
  //      n'étaient pas propagées dans le rapport mergé → bannière rouge
  //      invisible, l'utilisateur croit que le clic n'a rien fait.
  const runPerType = useCallback(async (applyFlag) => {
    const typeList = types.split(",").map((t) => t.trim()).filter(Boolean);
    const merged = {
      types: [],
      summary: {
        types_scanned: 0,
        docs_impacted_total: 0,
        step_removals_total: 0,
      },
      errors: [],
    };
    for (const name of typeList) {
      const params = new URLSearchParams({
        apply: String(applyFlag),
        types: name,
        expected_step_count: String(expected[name] ?? 0),
      });
      try {
        const { data } = await api.post(`/document-types/admin/cleanup-wrong-propagation?${params.toString()}`);
        if (data?.error) {
          merged.errors.push(`${name} — ${data.error}`);
        }
        if (data?.types?.length) merged.types.push(...data.types);
        if (data?.summary) {
          merged.summary.types_scanned       += data.summary.types_scanned       || 0;
          merged.summary.docs_impacted_total += data.summary.docs_impacted_total || 0;
          merged.summary.step_removals_total += data.summary.step_removals_total || 0;
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Erreur inconnue";
        merged.errors.push(`${name} — ${msg}`);
        toast.error(`Échec pour « ${name} »`, { description: msg });
      }
    }
    return merged;
  }, [types, expected]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runDryRun = useCallback(async () => {
    setLoading(true);
    setApplied(false);
    setReport(null);
    const data = await runPerType(false);
    setReport(data);
    if (data?.types?.length > 0) setExpandedType(data.types[0].type_id);
    setLoading(false);
  }, [runPerType]);

  // Dry-run initial à l'ouverture. On volontairement dépend UNIQUEMENT
  // de `open` (pas de `runDryRun`) pour ne pas déclencher plusieurs
  // fois : les changements de `expected` sont pilotés par le debounce
  // ci-dessous.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) { setApplied(false); runDryRun(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spec iter164y (2026-02-26) — Rafraîchissement automatique 500 ms
  // après la dernière modification d'une valeur « étapes attendues ».
  // Évite à l'admin de cliquer manuellement sur « Relancer le dry-run »
  // après chaque ajustement. Ignoré à l'ouverture (guard `didMount`).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const t = setTimeout(() => { runDryRun(); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expected, types, open]);

  useEffect(() => { if (!open) didMountRef.current = false; }, [open]);

  const applyFix = async () => {
    if (applying) return;
    setApplying(true);
    const data = await runPerType(true);
    setReport(data);
    setApplied(true);
    const n = data?.summary?.docs_impacted_total || 0;
    toast.success(`${n} document(s) corrigé(s)`, {
      description: "Backup sauvegardé dans documents_backup_iter161",
    });
    setApplying(false);
  };

  // Spec iter163 (Feb 2026) — restaure les documents depuis
  // documents_backup_iter161. Idempotent : peut être relancé sans risque.
  const doRollback = async () => {
    if (rollingBack) return;
    setRollingBack(true);
    setConfirmRollback(false);
    try {
      const { data } = await api.post("/document-types/admin/cleanup-wrong-propagation/rollback");
      const n = data?.restored_count || 0;
      toast.success(`${n} document(s) restauré(s)`, {
        description: n > 0
          ? "Contenu remis à l'état d'avant la correction iter161."
          : "Aucune sauvegarde à restaurer.",
      });
      // Refresh dry-run pour refléter l'état post-rollback.
      runDryRun();
    } catch (err) {
      toast.error("Rollback échoué", { description: err?.response?.data?.detail || "" });
    } finally {
      setRollingBack(false);
    }
  };

  const summary = report?.summary || {};
  const nDocs = summary.docs_impacted_total || 0;
  const nSteps = summary.step_removals_total || 0;
  const nTypesScanned = summary.types_scanned || 0;
  // Spec iter164B (2026-02-26) — GEL de la correction automatique.
  // Voir bandeau rouge en tête de la modale. `canApply` forcé à `false`
  // tant que la nouvelle méthode (basée sur les logs de propagation
  // datés + comparaison à `document.created_at`) n'est pas prête.
  const CLEANUP_APPLY_FROZEN = true;
  const canApply = !CLEANUP_APPLY_FROZEN && !applied && nDocs > 0 && !loading && !applying;

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        // Spec iter164i (2026-02-25) BUGFIX — retrait de la classe
        // `relative` : elle était mergée par tailwind-merge avec le
        // `fixed` par défaut de shadcn DialogContent → conflit sur
        // `position` résolu en gardant le DERNIER, ce qui rendait la
        // modale `position: relative` (positionnée in-flow dans le
        // document au lieu d'être centrée dans le viewport). Résultat :
        // sur les pages longues (ex : /documents/types), la modale
        // apparaissait AU BAS de la page, non-scrollable, invisible.
        //   → `position: fixed` fournit déjà un containing block pour
        //     l'overlay de confirmation Rollback (`absolute inset-0`),
        //     donc `relative` est totalement inutile ici.
        className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="cleanup-wrong-propagation-dialog"
        style={{ background: "#1B1B1B", border: "1px solid #2A2A2A" }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck size={16} style={{ color: "#5AA8E0" }} />
            Nettoyer les propagations parasites
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto pr-1">
          {/* Spec iter164B (2026-02-26) — AVERTISSEMENT CRITIQUE :
              l'heuristique par TAILLE (`expected_step_count`) a causé
              des dégâts en Prod : docs créés APRÈS l'ajout des étapes
              perdent leurs étapes légitimes. Bouton apply désactivé
              tant qu'une méthode fiable basée sur la vraie chronologie
              de propagation (logs `document_type_propagation_logs`)
              n'est pas conçue. Le dry-run + inspection + rollback
              restent disponibles pour audit. */}
          <div
            className="rounded-md px-3 py-2.5 text-xs flex items-start gap-2"
            style={{ background: "rgba(220,38,38,0.10)", border: "1.5px solid rgba(220,38,38,0.55)" }}
            data-testid="cleanup-danger-banner"
          >
            <AlertTriangle size={14} className="mt-[2px] shrink-0" style={{ color: "#F87171" }} />
            <div style={{ color: "#F87171", lineHeight: 1.45 }}>
              <b>Correction automatique désactivée — chantier gelé.</b><br />
              <span style={{ color: "#e0b0b0" }}>
                L'heuristique par « nombre d'étapes attendues » applique la même règle
                à TOUS les documents du type, sans distinguer ceux créés avant
                l'ajout des étapes (à garder) de ceux créés après (à ne PAS toucher).
                Sur Prod, des documents légitimes ont été raccourcis. Utilisez
                <b> Annuler la correction </b> pour restaurer, et attendez la nouvelle
                méthode basée sur les logs de propagation datés.
              </span>
            </div>
          </div>

          <div
            className="rounded-md px-3 py-2 text-[11px] flex items-start gap-2"
            style={{ background: "rgba(43,87,154,0.10)", border: "1px solid rgba(43,87,154,0.30)" }}
          >
            <AlertTriangle size={12} className="mt-[2px]" style={{ color: "#5AA8E0" }} />
            <div style={{ color: "#B7B7B7" }}>
              Le dry-run et l'inspection restent utilisables pour AUDITER, mais
              <b className="text-white/80"> Appliquer la correction est désactivé </b>
              tant qu'une méthode basée sur la vraie date de propagation n'est pas prête.
              Chaque document modifié précédemment reste sauvegardé dans <code>documents_backup_iter161</code>.
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              Types ciblés (CSV, noms exacts)
            </div>
            <Input
              value={types}
              onChange={(e) => setTypes(e.target.value)}
              disabled={loading || applying}
              className="text-xs"
              data-testid="cleanup-types-input"
              style={{ background: "#141414", border: "1px solid #2A2A2A" }}
            />
            {/* Spec iter164s (2026-02-25) — Nombre d'étapes ORIGINAL
                par type. Sert de fallback heuristique si le journal
                `document_type_propagation_logs` est incomplet
                (cas historique). Les étapes déjà validées
                (`closed_at`) sont TOUJOURS protégées. */}
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3 mb-1">
              Étapes d'origine attendues (par type)
            </div>
            <div className="space-y-1.5">
              {types.split(",").map((t) => t.trim()).filter(Boolean).map((name) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-[11px] flex-1 truncate" style={{ color: "#EDEDED" }}>{name}</span>
                  <Input
                    type="number"
                    min={1} max={50} step={1}
                    value={expected[name] ?? 0}
                    onChange={(e) => setExpected((prev) => ({
                      ...prev,
                      [name]: parseInt(e.target.value, 10) || 0,
                    }))}
                    disabled={loading || applying}
                    className="text-xs w-24"
                    data-testid={`cleanup-expected-${name}`}
                    style={{ background: "#141414", border: "1px solid #2A2A2A" }}
                  />
                  <span className="text-[10px] text-muted-foreground">étapes</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={runDryRun}
              disabled={loading || applying}
              className="mt-2 text-[11px] font-semibold"
              style={{ color: "#5AA8E0" }}
              data-testid="cleanup-refresh-dryrun"
            >
              {loading ? "Analyse…" : "↻ Relancer le dry-run"}
            </button>
          </div>

          {/* Bandeau résumé */}
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 size={14} className="animate-spin mr-2" /> Analyse en cours (dry-run, aucune modification)…
            </div>
          ) : report ? (
            <>
              <div className="grid grid-cols-3 gap-2" data-testid="cleanup-summary">
                {[
                  { label: "Types analysés", value: nTypesScanned, color: "#5AA8E0" },
                  { label: "Documents impactés", value: nDocs,    color: nDocs > 0 ? "#F97316" : "#22C55E" },
                  { label: "Étapes à retirer",  value: nSteps,    color: nSteps > 0 ? "#F97316" : "#22C55E" },
                ].map((k) => (
                  <div
                    key={k.label}
                    className="rounded-md p-2.5 text-center"
                    style={{ background: "#141414", border: "1px solid #2A2A2A" }}
                  >
                    <div className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                      {k.label}
                    </div>
                  </div>
                ))}
              </div>

              {applied && (
                <div
                  className="rounded-md px-3 py-2 text-[11px] flex items-center gap-2"
                  style={{ background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.30)" }}
                  data-testid="cleanup-applied-banner"
                >
                  <CheckCircle2 size={13} style={{ color: "#22C55E" }} />
                  <span style={{ color: "#86EFAC" }}>Correction appliquée avec succès. Rollback possible depuis <code>documents_backup_iter161</code>.</span>
                </div>
              )}

              {/* Erreurs back (par type non trouvé, etc.) — spec iter164u */}
              {report.errors?.length > 0 && (
                <div
                  className="rounded-md px-3 py-2 text-xs space-y-0.5"
                  style={{ background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.30)", color: "#F87171" }}
                  data-testid="cleanup-errors"
                >
                  {report.errors.map((e, i) => (
                    <div key={i}>• {e}</div>
                  ))}
                </div>
              )}

              {/* Détail par type */}
              {(report.types || []).map((t) => {
                const opened = expandedType === t.type_id;
                const impacted = t.documents_impacted || [];
                return (
                  <div
                    key={t.type_id}
                    className="rounded-md overflow-hidden"
                    style={{ background: "#141414", border: "1px solid #2A2A2A" }}
                    data-testid="cleanup-type-block"
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedType(opened ? null : t.type_id)}
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/[0.03]"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {opened
                          ? <ChevronDown size={13} className="shrink-0 text-muted-foreground" />
                          : <ChevronRight size={13} className="shrink-0 text-muted-foreground" />}
                        <div className="text-xs font-semibold text-white/90 truncate">{t.type_name}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 text-[11px]">
                        <span className="text-muted-foreground">{t.docs_scanned} scannés</span>
                        <span
                          className="px-2 py-0.5 rounded-full font-bold"
                          style={{
                            background: impacted.length > 0 ? "rgba(249,115,22,0.15)" : "rgba(34,197,94,0.10)",
                            color:      impacted.length > 0 ? "#FB923C"               : "#86EFAC",
                          }}
                        >
                          {impacted.length} impacté{impacted.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </button>
                    {opened && (
                      <div className="border-t px-3 py-2 max-h-64 overflow-y-auto" style={{ borderColor: "#2A2A2A" }}>
                        {impacted.length === 0 ? (
                          <div className="text-[11px] text-muted-foreground py-2">
                            Aucun document impacté pour ce type. ✓
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {impacted.map((d) => (
                              <div
                                key={d.id}
                                className="rounded px-2 py-1.5 text-[11px]"
                                style={{ background: "#1A1A1A", border: "1px solid #2A2A2A" }}
                                data-testid="cleanup-doc-row"
                              >
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-mono font-bold" style={{ color: "#5AA8E0" }}>{d.reference}</span>
                                  <span className="text-white/70 truncate">{d.title}</span>
                                  <span className="ml-auto text-[10px] text-muted-foreground">{d.status}</span>
                                </div>
                                <div className="text-muted-foreground">
                                  Étape{d.steps_to_remove.length > 1 ? "s" : ""} à retirer :{" "}
                                  {d.steps_to_remove.map((s, i) => {
                                    // Spec iter164z — badge détection : log
                                    // (fiable, du journal) vs heuristique
                                    // (déduit du nombre d'étapes en excès).
                                    const isHeuristic = s.detection === "size_heuristic";
                                    return (
                                      <span key={s.step_id} className="inline-flex items-center gap-1 mr-1.5">
                                        <b className="text-white/80">{s.step_label || "(sans nom)"}</b>
                                        <span
                                          className="inline-flex items-center rounded-sm px-1 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                                          style={{
                                            background: isHeuristic ? "rgba(249,115,22,0.15)" : "rgba(90,168,224,0.15)",
                                            color:      isHeuristic ? "#FB923C"               : "#5AA8E0",
                                            border:     isHeuristic ? "1px solid rgba(249,115,22,0.35)" : "1px solid rgba(90,168,224,0.35)",
                                            lineHeight: 1,
                                          }}
                                          title={isHeuristic
                                            ? "Détection HEURISTIQUE — dérivée du nombre d'étapes attendues (moins fiable que les logs)"
                                            : "Détection LOG — journal document_type_propagation_logs (source de vérité)"}
                                          data-testid={`cleanup-step-detection-${s.step_id}`}
                                        >
                                          {isHeuristic ? "heuristique" : "log"}
                                        </span>
                                        {i < d.steps_to_remove.length - 1 ? " " : ""}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : null}
        </div>

        <DialogFooter className="gap-2 pt-3 border-t" style={{ borderColor: "#2A2A2A" }}>
          {/* Spec iter163 · point 6 — bouton Rollback à gauche */}
          <button
            type="button"
            onClick={() => setConfirmRollback(true)}
            disabled={applying || rollingBack || loading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold transition-colors disabled:opacity-50 mr-auto"
            style={{
              background: "transparent",
              border: "1.5px solid #EA580C",
              color: "#FB923C",
            }}
            data-testid="cleanup-rollback-btn"
            title="Restaurer les documents depuis la sauvegarde documents_backup_iter161"
          >
            {rollingBack ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
            Annuler la correction
          </button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying || rollingBack}
            data-testid="cleanup-close-btn"
          >
            Fermer
          </Button>
          <Button
            onClick={applyFix}
            disabled={!canApply || rollingBack}
            data-testid="cleanup-apply-btn"
            style={{ background: canApply ? "#DC2626" : "#4B5563", color: "white", cursor: canApply ? "pointer" : "not-allowed" }}
            title={CLEANUP_APPLY_FROZEN ? "Correction automatique désactivée — méthode fiable en cours de conception" : applied ? "Déjà appliqué" : canApply ? "Appliquer la correction avec backup" : "Aucun document impacté"}
          >
            {applying ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Application…</span>
            ) : applied ? (
              "Appliqué ✓"
            ) : CLEANUP_APPLY_FROZEN ? (
              "Correction désactivée"
            ) : (
              `Appliquer la correction${nDocs > 0 ? ` (${nDocs} doc${nDocs > 1 ? "s" : ""})` : ""}`
            )}
          </Button>
        </DialogFooter>

        {/* Confirmation rollback — inline pour ne pas empiler 2 Dialogs Radix. */}
        {confirmRollback && (
          <div
            className="absolute inset-0 flex items-center justify-center z-10 rounded-lg"
            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(3px)" }}
            data-testid="cleanup-rollback-confirm"
            onClick={() => setConfirmRollback(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="rounded-lg p-4 max-w-md"
              style={{ background: "#1A1A1A", border: "2px solid #EA580C" }}
            >
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={16} className="mt-0.5" style={{ color: "#EA580C" }} />
                <div>
                  <div className="text-sm font-bold text-white/90">Annuler la correction ?</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Restaure tous les documents depuis la collection
                    <code className="mx-1 px-1 rounded bg-black/30">documents_backup_iter161</code>
                    à l'état d'avant l'application iter161. Cette action est <b>idempotente</b>
                    (peut être relancée sans risque).
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmRollback(false)}
                  data-testid="cleanup-rollback-cancel"
                >
                  Non
                </Button>
                <Button
                  size="sm"
                  onClick={doRollback}
                  data-testid="cleanup-rollback-confirm-btn"
                  style={{ background: "#EA580C", color: "white" }}
                >
                  Oui, restaurer
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
